import type * as React from 'react';

export enum FloatingPanelPositionEnum {
	TOP_RIGHT = 'top-right',
	TOP_LEFT = 'top-left',
	BOTTOM_RIGHT = 'bottom-right',
	BOTTOM_LEFT = 'bottom-left',
}

export enum FloatingPanelStateEnum {
	HIDDEN = 'hidden',
	COLLAPSED = 'collapsed',
	EXPANDED = 'expanded',
}

export enum FloatingPanelToneEnum {
	SUCCESS = 'success',
	WARNING = 'warning',
	ERROR = 'error',
	LOADING = 'loading',
}

export interface IFloatingNotificationPanelLabels {
	/** Title shown in the expanded panel header */
	title: string;
	/** Empty state content when no children. Accepts a string or any ReactNode, including block-level layouts. */
	emptyState: React.ReactNode;
	/** Aria label for the minimize control */
	minimizeAriaLabel: string;
	/** Aria label for dismiss/close controls */
	dismissAriaLabel: string;
	/**
	 * Aria label for the collapsed badge.
	 * Receives the current badge count and the panel tone so warning/error are not color-only.
	 */
	expandAriaLabel: (count: number, tone: FloatingPanelToneEnum) => string;
	/**
	 * Accessible name for the expanded header tone indicator.
	 */
	toneAriaLabel: (tone: FloatingPanelToneEnum) => string;
	/** Aria label for moving the panel from the top to the bottom of the page */
	moveToBottomAriaLabel: string;
	/** Aria label for moving the panel from the bottom to the top of the page */
	moveToTopAriaLabel: string;
}

export interface IFloatingNotificationPanelProps {
	/** Current state of the panel */
	state: FloatingPanelStateEnum;
	/** Position of the panel on screen */
	position?: FloatingPanelPositionEnum;
	/** User-facing copy and aria labels (required; no package defaults) */
	labels: IFloatingNotificationPanelLabels;
	/** Content to render when expanded (typically a list of NotificationItems) */
	children?: React.ReactNode;
	/** Trailing action at the end of the scrollable content, e.g. a view-all link */
	footer?: React.ReactNode;
	/** Count to show on the collapsed badge */
	badgeCount?: number;
	/**
	 * Visual tone for the collapsed icon, collapsed count badge, and expanded header indicator.
	 * Defaults to success (green).
	 */
	tone?: FloatingPanelToneEnum;
	/** Icon to show on the collapsed badge */
	badgeIcon?: React.ReactNode;
	/** Callback when the collapsed badge is clicked (typically to expand) */
	onBadgeClick?: () => void;
	/** Callback when the minimize button is clicked (collapse to badge) */
	onMinimize?: () => void;
	/** Callback when the dismiss/close button is clicked (hide completely) */
	onDismiss?: () => void;
	/** Callback when the move-position control is clicked (flip top/bottom on the same side) */
	onTogglePosition?: () => void;
	/** Whether to show pulse animation on the badge (e.g., new notification) */
	shouldPulse?: boolean;
	/** Max height of the content area when expanded */
	maxContentHeight?: string;
	/** Width of the expanded panel */
	panelWidth?: string;
	/** Z-index for the panel */
	zIndex?: number;
	/** Additional CSS classes for the container */
	className?: string;
	/** Test ID for testing */
	testId?: string;
}

export interface IPositionMoveControl {
	position: FloatingPanelPositionEnum;
	onToggle: () => void;
	moveToBottomAriaLabel: string;
	moveToTopAriaLabel: string;
}
